/**
 * A room row's "…" menu (a file, a picture, a pin): the Workshop menu on the
 * desk, a sheet headed by the row on a phone. The destructive item sits apart
 * at the end.
 */
import { useState } from "react";
import { Button, Menu, MenuItem, MenuTrigger, Separator, type Key } from "react-aria-components";
import { MoreHorizontal, type LucideIcon } from "lucide-react";
import { KitModal } from "@/app/routes/dashboard/components/kit/Modal";
import { KitPopover } from "@/app/routes/dashboard/components/kit/Popover";

export type RowMenuItem = {
  id: string;
  label: string;
  icon: LucideIcon;
  danger?: boolean;
  disabled?: boolean;
};

type Props = {
  /** "Actions for Untitled_spreadsheet.csv" */
  label: string;
  /** The sheet's heading on a phone. */
  title: string;
  meta?: string;
  items: RowMenuItem[];
  asSheet: boolean;
  onAction: (id: string) => void;
};

export default function WorkshopRowMenu({ label, title, meta, items, asSheet, onAction }: Props) {
  const [open, setOpen] = useState(false);
  const main = items.filter((item) => !item.danger);
  const danger = items.filter((item) => item.danger);

  if (asSheet) {
    const run = (id: string) => {
      setOpen(false);
      onAction(id);
    };
    return (
      <>
        <button
          type="button"
          className="ws-card-menu-trigger ws-crow-more"
          aria-label={label}
          aria-haspopup="dialog"
          onClick={() => setOpen(true)}
        >
          <MoreHorizontal className="size-4" strokeWidth={2} aria-hidden="true" />
        </button>
        <KitModal
          isOpen={open}
          onOpenChange={setOpen}
          aria-label={label}
          hideCloseButton
          modalClassName="ws-dialog ws-action-sheet"
          overlayClassName="ws-dialog-overlay"
        >
          <span className="ws-sheet-grabber" aria-hidden="true" />
          <div className="ws-action-sheet-head">
            <p className="ws-action-sheet-title">{title}</p>
            {meta ? <p className="ws-action-sheet-meta">{meta}</p> : null}
          </div>
          <div className="ws-action-sheet-list">
            {main.map((item) => (
              <button
                key={item.id}
                type="button"
                className="ws-action-sheet-item"
                disabled={item.disabled}
                onClick={() => run(item.id)}
              >
                <item.icon className="size-[18px]" strokeWidth={1.7} aria-hidden="true" />
                {item.label}
              </button>
            ))}
            {danger.length > 0 && main.length > 0 ? (
              <span className="ws-action-sheet-sep" aria-hidden="true" />
            ) : null}
            {danger.map((item) => (
              <button
                key={item.id}
                type="button"
                className="ws-action-sheet-item is-danger"
                disabled={item.disabled}
                onClick={() => run(item.id)}
              >
                <item.icon className="size-[18px]" strokeWidth={1.7} aria-hidden="true" />
                {item.label}
              </button>
            ))}
          </div>
        </KitModal>
      </>
    );
  }

  return (
    <MenuTrigger>
      <Button aria-label={label} className="ws-card-menu-trigger ws-crow-more">
        <MoreHorizontal className="size-4" strokeWidth={2} aria-hidden="true" />
      </Button>
      <KitPopover placement="bottom end" offset={6} className="wb-glass-pop ws-menu ws-menu--wide">
        <Menu
          aria-label={label}
          className="ws-menu-list"
          disabledKeys={items.filter((item) => item.disabled).map((item) => item.id)}
          onAction={(key: Key) => onAction(String(key))}
        >
          {main.map((item) => (
            <MenuItem key={item.id} id={item.id} textValue={item.label} className="ws-menu-item">
              <item.icon className="size-3.5" strokeWidth={1.8} aria-hidden="true" />
              {item.label}
            </MenuItem>
          ))}
          {danger.length > 0 && main.length > 0 ? <Separator className="ws-menu-sep" /> : null}
          {danger.map((item) => (
            <MenuItem
              key={item.id}
              id={item.id}
              textValue={item.label}
              className="ws-menu-item is-danger"
            >
              <item.icon className="size-3.5" strokeWidth={1.8} aria-hidden="true" />
              {item.label}
            </MenuItem>
          ))}
        </Menu>
      </KitPopover>
    </MenuTrigger>
  );
}
